import {z as zod} from "zod";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {
    ImageDetails,
    Media,
    PdfDetails,
    UnknownDetails,
    VideoDetails,
} from "@vesta/janus-compatibility-module/utilities/typeDefinitions";
import {MediaType} from "@vesta/janus-compatibility-module/utilities/mediaTypes";

const uuidZodValidator = zod.custom<Uuid>((input) => {
    const parser = zod.string().uuid();
    const parsedValueResult = parser.safeParse(input);
    return parsedValueResult.success === true;
});

export const imageDetailsValidator = zod
    .object({
        width: zod.number(),
        height: zod.number(),
        altText: zod.string().optional(),
        title: zod.string().optional(),
        variants: zod.array(
            zod.object({
                width: zod.number(),
                height: zod.number(),
                dpi: zod.number().optional(),
                format: zod.string().optional(),
                url: zod.string(),
                fileSize: zod.number(),
            }),
        ),
    })
    .transform((validatedRow) => {
        const imageDetails: ImageDetails = validatedRow;
        return imageDetails;
    });

export const pdfDetailsValidator = zod
    .object({
        pageCount: zod.number().optional(),
        variants: zod.array(
            zod.object({
                fileSize: zod.number(),
                url: zod.string(),
            }),
        ),
    })
    .transform((validatedRow) => {
        const pdfDetails: PdfDetails = validatedRow;
        return pdfDetails;
    });

export const videoDetailsValidator = zod
    .object({
        width: zod.number(),
        height: zod.number(),
        variants: zod.array(
            zod.object({
                width: zod.number(),
                height: zod.number(),
                format: zod.string().optional(),
                fps: zod.number().optional(),
                fileSize: zod.number(),
                url: zod.string(),
            }),
        ),
    })
    .transform((validatedRow) => {
        const videoDetails: VideoDetails = validatedRow;
        return videoDetails;
    });

export const unknownDetailsValidator = zod
    .object({
        fileSize: zod.number(),
        url: zod.string(),
    })
    .transform((validatedRow) => {
        const unknownDetails: UnknownDetails = validatedRow;
        return unknownDetails;
    });

export const mediaValidator = zod
    .object({
        id: uuidZodValidator,
        created_at: zod.date(),
        updated_at: zod.date(),
        display_name: zod.string(),
        tags: zod.array(zod.string()),
        is_deleted: zod.boolean(),
        original_file_hash: zod.string(),
        original_file_url: zod.string(),
        original_file_size: zod.coerce.number(),
        thumbnail_url: zod.string().nullable(),
        thumbnail_width: zod.coerce.number(),
        thumbnail_height: zod.coerce.number(),
        parent_folder_id: uuidZodValidator.nullable(),
        environment: zod.string().nullable(),
    })
    .and(
        zod.union([
            zod.object({
                media_type: zod.literal(MediaType.image),
                data: imageDetailsValidator,
                format: zod.string(),
            }),
            zod.object({
                media_type: zod.literal(MediaType.video),
                data: videoDetailsValidator,
                format: zod.string(),
            }),
            zod.object({
                media_type: zod.literal(MediaType.pdf),
                data: pdfDetailsValidator,
                format: zod.string(),
            }),
            zod.object({
                media_type: zod.literal(MediaType.unknown),
                data: unknownDetailsValidator,
                format: zod.null(),
            }),
        ]),
    )
    .transform((validatedRow) => {
        const media = {
            id: validatedRow.id,
            createdAt: validatedRow.created_at.toISOString() as Iso8601DateTime,
            updatedAt: validatedRow.updated_at.toISOString() as Iso8601DateTime,
            displayName: validatedRow.display_name,
            tags: validatedRow.tags,
            isDeleted: validatedRow.is_deleted,
            originalFileHash: validatedRow.original_file_hash,
            format: validatedRow.format,
            originalFileSize: validatedRow.original_file_size,
            originalFileUrl: validatedRow.original_file_url,
            thumbnailUrl: validatedRow.thumbnail_url,
            thumbnailWidth: validatedRow.thumbnail_width,
            thumbnailHeight: validatedRow.thumbnail_height,
            parentFolderId: validatedRow.parent_folder_id,
            mediaType: validatedRow.media_type as Media["mediaType"],
            data: validatedRow.data,
            environment: validatedRow.environment,
        } as Media;
        return media;
    });
